// src/platform/dashboards/WellfinderReviewDashboard.jsx
//
// The WellFinder adjudication dashboard.
//
// Fetches its own data from /api/v1/platform/ai-dashboards/wellfinder-review on
// mount — the landing page hands it nothing but a key. That is what makes card
// selection repopulate rather than replay: the rollup on the card and the detail
// on this page are separate reads, so opening a dashboard always shows the
// corpus as it is now, not as it was when the grid loaded. The corpus is live
// (analysts commit continuously), so `generated_at` is displayed rather than
// implied.

import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, AlertTriangle, Info } from "lucide-react";
import { fetchWithAuth } from "../../api/fetchWithAuth";
import {
  Panel, Stat, MetricRow, SegmentedBar, ColumnChart, MiniBars,
  DataTable, Legend,
} from "./primitives";
import { TONE, fmt, pct } from "./format";

// Analyst series colours. A fixed list indexed by position in the (count-sorted)
// analyst array — never cycled, so an analyst keeps their colour as the roster
// grows. Past this list a series folds into "Other" rather than inventing a hue.
const SERIES = ["#1f9dff", "#f97316", "#16f2d4", "#a78bfa", "#f472b6"];

const TIER_LABEL = { a: "Tier A", a_prime: "Tier A′", b: "Tier B", c: "Tier C" };

const STATUS_TONE = {
  approved: "good",
  needs_review: "attention",
  rejected: "bad",
  pending: "idle",
};

export default function WellfinderReviewDashboard({ dashboardKey = "wellfinder-review" }) {
  const [state, setState] = useState({ loading: true, error: null, payload: null });
  // "" is All wells. Kept as the empty string rather than null so it round-trips
  // through <select value> without React switching the control to uncontrolled.
  const [aoiId, setAoiId] = useState("");

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const qs = aoiId ? `?operating_aoi_id=${encodeURIComponent(aoiId)}` : "";
      const res = await fetchWithAuth(
        `/api/v1/platform/ai-dashboards/${encodeURIComponent(dashboardKey)}${qs}`,
      );
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      const payload = await res.json();
      setState({ loading: false, error: null, payload });
    } catch (err) {
      setState({ loading: false, error: err.message || "Failed to load", payload: null });
    }
  }, [dashboardKey, aoiId]);

  useEffect(() => { load(); }, [load]);

  if (state.loading && !state.payload) {
    return <div className="p-6 text-sm text-fg-secondary">Loading dashboard…</div>;
  }
  if (state.error) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3">
          <div className="text-sm text-red-300 font-medium">Could not load this dashboard</div>
          <div className="text-xs text-fg-secondary mt-1">{state.error}</div>
          <button onClick={load}
                  className="mt-3 text-xs px-3 py-1.5 rounded border border-bg-border
                             bg-bg-soft hover:bg-bg-softer text-fg-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const d = state.payload.data;

  // ── derived ────────────────────────────────────────────────────────────────
  const byStatus = Object.fromEntries(d.status.map((s) => [s.key, s.n]));
  const total = d.status.reduce((s, x) => s + x.n, 0);
  const reviewed = total - (byStatus.pending ?? 0);

  const colorFor = {};
  d.analysts.forEach((a, i) => { colorFor[a.name] = SERIES[i] ?? "#64748b"; });

  // Daily rows arrive as one row per (day, analyst); the chart wants one column
  // per day with its analysts stacked inside it.
  const days = [];
  for (const row of d.daily) {
    let day = days.find((x) => x.key === row.day);
    if (!day) { day = { key: row.day, label: shortDay(row.day), parts: [] }; days.push(day); }
    day.parts.push({ name: row.name, value: row.n });
  }

  const busiest = days.reduce(
    (best, x) => {
      const t = x.parts.reduce((s, p) => s + p.value, 0);
      return t > best.n ? { label: x.label, n: t } : best;
    }, { label: "—", n: 0 });

  const matchTotal = d.match_confidence.reduce((s, x) => s + x.n, 0);
  // The AOI the SERVER applied, not the one the control asked for — they differ
  // if a stale id was dropped, and the headline must name what was counted.
  const activeAoi = (d.operating_aois ?? []).find((a) => a.id === d.operating_aoi_id) ?? null;
  const paceByTier = Object.fromEntries(d.pace_by_tier.map((p) => [p.tier, p.median_s]));

  return (
    <div className="p-6 space-y-6 max-w-[1400px]">

      {/* ── headline ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-fg-secondary font-medium">
            Corpus reviewed
          </div>
          <div className="text-5xl font-semibold tracking-tight tabular-nums text-fg-primary leading-none mt-1">
            {pct(reviewed, total).toFixed(1)}
            <span className="text-xl text-fg-secondary font-medium">%</span>
          </div>
        </div>
        <p className="text-xs text-fg-secondary max-w-md leading-relaxed">
          {fmt(reviewed)} of {fmt(total)} wellhead detections have a verdict
          {activeAoi ? <> in <b className="text-fg-primary">{activeAoi.name}</b></> : null}.
          One extraction run against the Texas RRC surface registry.
        </p>
        {/* ── OPERATING AOI ──────────────────────────────────────────────────
            Sits with the headline, not in the panels, because it changes what
            EVERY figure below counts — a filter that looks like one panel's
            control reads as if it only narrows that panel. Selecting refetches;
            nothing here is filtered client-side, so the numbers cannot be a
            subset of a stale payload.

            Counts come from the same predicate the filter applies, so the
            option label and the total you land on agree. */}
        <label className="ml-auto flex items-center gap-2 text-[11px] text-fg-secondary">
          <span className="font-mono uppercase tracking-wider">Operating AOI</span>
          <select
            id="wf-operating-aoi"
            value={aoiId}
            onChange={(e) => setAoiId(e.target.value)}
            disabled={state.loading}
            className="bg-bg-soft border border-bg-border rounded px-2 py-1 text-[11px]
                       text-fg-primary hover:bg-bg-softer focus:outline-none
                       focus:ring-1 focus:ring-accent-info/70 disabled:opacity-50"
          >
            <option value="">All wells ({fmt(total)})</option>
            {(d.operating_aois ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} ({fmt(a.record_count)})
              </option>
            ))}
          </select>
        </label>

        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded
                     border border-bg-border bg-bg-soft hover:bg-bg-softer text-fg-secondary
                     hover:text-fg-primary transition disabled:opacity-50"
          disabled={state.loading}
        >
          <RefreshCw size={12} className={state.loading ? "animate-spin" : ""} />
          {state.loading ? "Refreshing" : "Refresh"}
        </button>
      </div>

      <div>
        <SegmentedBar
          height="h-6"
          total={total}
          segments={["approved", "needs_review", "rejected", "pending"].map((k) => ({
            key: k, label: k.replace("_", " "), value: byStatus[k] ?? 0, tone: STATUS_TONE[k],
          }))}
        />
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-[11px] text-fg-secondary">
          {["approved", "needs_review", "rejected", "pending"].map((k) => (
            <span key={k} className="inline-flex items-center gap-1.5">
              <i className={`w-2.5 h-2.5 rounded-sm ${TONE[STATUS_TONE[k]].fill}`} />
              {k.replace("_", " ")}
              <b className="tabular-nums text-fg-primary font-medium">{fmt(byStatus[k] ?? 0)}</b>
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-bg-border rounded-lg overflow-hidden border border-bg-border">
        <div className="bg-bg-soft"><Stat label="Reviewed" value={fmt(reviewed)}
          sub={`${d.analysts.length} analyst${d.analysts.length === 1 ? "" : "s"}`} /></div>
        <div className="bg-bg-soft"><Stat label="Remaining" value={fmt(byStatus.pending ?? 0)}
          sub={`${pct(byStatus.pending ?? 0, total).toFixed(1)}% of corpus`} tone="idle" /></div>
        <div className="bg-bg-soft"><Stat label="Registry matches" value={fmt(matchTotal)}
          sub={d.match_confidence.map((m) => `${fmt(m.n)} ${m.key}`).join(" · ")} /></div>
        <div className="bg-bg-soft"><Stat label="Needs review" value={fmt(byStatus.needs_review ?? 0)}
          sub={d.exceptions[0]?.key.split(" · ")[1] ?? "—"}
          tone={(byStatus.needs_review ?? 0) > 0 ? "attention" : "idle"} /></div>
      </div>

      {/* ── analysts ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-[1.35fr_1fr] gap-6 items-start">
        <Panel title="Daily throughput" sub="Records committed per day, stacked by analyst">
          <Legend items={d.analysts.map((a) => ({ label: a.name, color: colorFor[a.name] }))} />
          <ColumnChart data={days} seriesColor={(n) => colorFor[n] ?? "#64748b"} />
          <p className="text-[11px] text-fg-secondary mt-3 leading-relaxed">
            {busiest.label} is the peak at <b className="text-fg-primary">{fmt(busiest.n)}</b> records
            — {pct(busiest.n, reviewed).toFixed(0)}% of all output. Days with no commits are omitted.
          </p>
        </Panel>

        <Panel title="Working hours" sub="Commits by hour of day, UTC · all analysts">
          <MiniBars data={d.hours.map((h) => ({
            label: String(h.hour).padStart(2, "0"), value: h.n,
          }))} />
          <p className="text-[11px] text-fg-secondary mt-3">
            Hours with no commits are omitted.
          </p>
        </Panel>
      </div>

      <Panel title="Per-analyst detail"
             sub="Pace is the median gap between consecutive commits, excluding gaps over 30 minutes">
        <DataTable
          columns={[
            { key: "name", label: "Analyst", render: (r) => (
              <span className="inline-flex items-center gap-2">
                <i className="w-2.5 h-2.5 rounded-sm shrink-0"
                   style={{ background: colorFor[r.name] }} />
                {r.name}
              </span>
            ) },
            { key: "total",    label: "Records",  render: (r) => fmt(r.total) },
            { key: "approved", label: "Approved", render: (r) => fmt(r.approved) },
            { key: "flagged",  label: "Flagged",  render: (r) => fmt(r.flagged) },
            { key: "rejected", label: "Rejected", render: (r) => fmt(r.rejected) },
            { key: "days",     label: "Days",     render: (r) => fmt(r.days) },
            { key: "median_s", label: "Median",   render: (r) => r.median_s != null ? `${r.median_s}s` : "—" },
            { key: "p90_s",    label: "p90",      render: (r) => r.p90_s != null ? `${r.p90_s}s` : "—" },
            { key: "active_hours", label: "Active", render: (r) => r.active_hours != null ? `${r.active_hours}h` : "—" },
          ]}
          rows={d.analysts}
        />
      </Panel>

      {/* ── queue + geometry ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <Panel title="Completion by tier" sub="Each bar is one tier's full population">
          {d.tiers.map((t) => {
            const done = t.approved + t.flagged + t.rejected;
            return (
              <MetricRow
                key={t.tier}
                label={TIER_LABEL[t.tier] ?? t.tier}
                sublabel={`${fmt(t.total)} records${
                  paceByTier[t.tier] != null ? ` · ${paceByTier[t.tier]}s median` : ""}`}
                total={t.total}
                value={`${pct(done, t.total).toFixed(1)}%`}
                segments={[
                  { key: "approved", label: "approved",     value: t.approved, tone: "good" },
                  { key: "flagged",  label: "needs review", value: t.flagged,  tone: "attention" },
                  { key: "rejected", label: "rejected",     value: t.rejected, tone: "bad" },
                  { key: "pending",  label: "pending",      value: t.pending,  tone: "idle" },
                ]}
              />
            );
          })}
        </Panel>

        <Panel title="Candidate geometry"
               sub="Distance from detection to its rank-1 registry candidate, in metres">
          <DataTable
            columns={[
              { key: "tier", label: "Tier", render: (r) => TIER_LABEL[r.tier] ?? r.tier },
              { key: "n",    label: "Records", render: (r) => fmt(r.n) },
              { key: "p50",  label: "p50", render: (r) => r.p50 == null ? "—" : `${r.p50} m` },
              { key: "p90",  label: "p90", render: (r) => r.p90 == null ? "—" : `${r.p90} m` },
              { key: "max",  label: "Max", render: (r) => r.max == null ? "—" : `${r.max} m` },
            ]}
            rows={d.distance}
          />
          <p className="text-[11px] text-fg-secondary mt-3 leading-relaxed">
            Tier C has no distance because it has no candidates — undefined, not missing.
            {" "}{fmt(d.candidates.rows)} candidates across {fmt(d.candidates.detections)} detections
            (mean {d.candidates.avg_per_detection}, max {d.candidates.max_per_detection}).
          </p>
        </Panel>
      </div>

      {/* ── output ────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 items-start">
        <Panel title="Attribute coverage"
               sub={`Share of the ${fmt(d.coverage.reviewed)} reviewed records carrying each adjudicated field`}>
          {["equipment_type", "api10", "well_status", "oil_gas_code", "geom_adjusted",
            "detection_confidence"].map((k) => (
            <MetricRow
              key={k}
              label={k}
              sublabel={`${fmt(d.coverage[k])} / ${fmt(d.coverage.reviewed)}`}
              total={d.coverage.reviewed}
              value={`${pct(d.coverage[k], d.coverage.reviewed).toFixed(1)}%`}
              segments={[{
                key: k, label: k, value: d.coverage[k],
                tone: d.coverage[k] === 0 ? "bad" : "neutral",
              }]}
            />
          ))}
        </Panel>

        <Panel title="Exceptions" sub="Every non-approval, and every edit to platform-generated geometry">
          <DataTable
            columns={[
              { key: "key", label: "Outcome" },
              { key: "n",   label: "Count", render: (r) => fmt(r.n) },
            ]}
            rows={[
              ...d.exceptions,
              ...d.events
                .filter((e) => ["reopened", "match_downgraded", "geometry_adjusted",
                                "boundary_adjusted", "note_added"].includes(e.key))
                .map((e) => ({ key: e.key, n: e.n })),
            ]}
          />
        </Panel>
      </div>

      <Panel title="Event log"
             sub={`${fmt(d.events.reduce((s, e) => s + e.n, 0))} events · one row per action, not per record`}>
        {d.events.map((e) => (
          <MetricRow
            key={e.key}
            label={e.key}
            sublabel={`${fmt(e.records)} records`}
            total={d.events[0].n}
            value={fmt(e.n)}
            segments={[{
              key: e.key, label: e.key, value: e.n,
              tone: e.key === "assigned" ? "idle" : "neutral",
            }]}
          />
        ))}
      </Panel>

      {/* ── caveats, from the API ─────────────────────────────────────────── */}
      {d.notes?.length ? (
        <Panel title="Reading these numbers" sub="Caveats that travel with the data">
          <ul className="space-y-2.5">
            {d.notes.map((n, i) => (
              <li key={i} className="flex gap-2.5 text-[12px] text-fg-secondary leading-relaxed">
                {i === 0 && d.coverage.detection_confidence === 0
                  ? <AlertTriangle size={13} className="text-amber-400 shrink-0 mt-0.5" />
                  : <Info size={13} className="text-fg-secondary shrink-0 mt-0.5" />}
                <span>{n}</span>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      <p className="text-[11px] text-fg-secondary">
        Generated {new Date(d.generated_at).toLocaleString()} · the corpus is live, so these
        figures move while you read them. Test accounts are excluded from analyst figures.
      </p>
    </div>
  );
}

// "2026-09-15" → "15 Sep". Built from the parts rather than Date parsing, which
// would shift the label by a day for anyone west of UTC.
function shortDay(iso) {
  const M = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
             "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const [, m, d] = iso.split("-");
  return `${Number(d)} ${M[Number(m) - 1] ?? "?"}`;
}
